import React from 'react';
import { Image, FileText, File, Archive } from 'lucide-react';
import { Attachment } from '../models/Content';
import { storage } from '../config/firebase.ts';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FILE_LIMITS, ERROR_MESSAGES } from '../config/constants';

/**
 * Formats file size from bytes to human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Returns appropriate icon for file type
 */
export const getFileIcon = (type: string): JSX.Element => {
  if (type.startsWith('image/')) return <Image className="w-4 h-4" />;
  if (type === 'application/pdf') return <FileText className="w-4 h-4" />;
  if (type.includes('spreadsheet') || type.includes('excel')) return <File className="w-4 h-4" />;
  if (type.includes('zip') || type.includes('rar')) return <Archive className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
};

/**
 * Determines if a file type can be previewed
 */
export const canPreviewFile = (type: string): boolean => {
  // Images
  if (type.startsWith('image/')) return true;
  
  // PDFs
  if (type === 'application/pdf') return true;
  
  // Text files
  if (type.startsWith('text/')) return true;
  
  // Common document types that browsers can handle
  if (type === 'application/msword' || 
      type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return false; // Word docs need special handling, disable for now
  }
  
  // Video files
  if (type.startsWith('video/')) return true;
  
  // Audio files  
  if (type.startsWith('audio/')) return true;
  
  return false;
};

/**
 * Gets the preview type for a file
 */
export const getPreviewType = (type: string): 'image' | 'pdf' | 'text' | 'video' | 'audio' | 'unsupported' => {
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('text/')) return 'text';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'unsupported';
};

/**
 * Handles file upload from input change event
 */
export const handleFileUpload = async (
  e: React.ChangeEvent<HTMLInputElement>,
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>
) => {
  if (e.target.files) {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      // Validate file
      const validation = validateFile(file);
      if (!validation.isValid) {
        console.error(validation.error);
        continue;
      }

      try {
        // Create a unique file name
        const fileName = `${Date.now()}-${file.name}`;
        const storageRef = ref(storage, `attachments/${fileName}`);
        
        // Upload file to Firebase Storage
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        // Create attachment object
        const newAttachment: Attachment = {
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          url: downloadURL,
          type: file.type,
          size: file.size
        };
        
        setAttachments(prev => [...prev, newAttachment]);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  }
};

/**
 * Removes attachment by ID
 */
export const removeAttachment = (
  attachmentId: string,
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>
) => {
  setAttachments(prev => prev.filter(att => att.id !== attachmentId));
};

/**
 * Drag and drop event handlers
 */
export const createDragHandlers = (
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>
) => {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const newAttachments: Attachment[] = files.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
        size: file.size
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  return {
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop
  };
};

/**
 * Downloads a file attachment
 */
export const downloadAttachment = (attachment: Attachment) => {
  const link = document.createElement('a');
  link.href = attachment.url;
  link.download = attachment.name;
  link.click();
};

/**
 * File validation constants and function
 */
export const FILE_VALIDATION = {
  ACCEPTED_TYPES: FILE_LIMITS.ACCEPTED_TYPES,
  MAX_SIZE: FILE_LIMITS.MAX_SIZE,
  DESCRIPTION: FILE_LIMITS.DESCRIPTION
};

/**
 * Validate file size and type
 */
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file size
  if (file.size > FILE_LIMITS.MAX_SIZE) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.FILE_TOO_LARGE
    };
  }

  // Check file type
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
  const acceptedTypes = FILE_LIMITS.ACCEPTED_TYPES.split(',');
  
  if (!acceptedTypes.includes(fileExtension)) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.INVALID_FILE_TYPE
    };
  }

  return { isValid: true };
}; 